import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CATEGORIES, type Category, type Recipe, type RecipeInput } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import PhotoUploader from '../components/PhotoUploader';
import IconPicker from '../components/IconPicker';

type DraftIngredient = {
  id?: string;
  name: string;
  quantity: number | '';
  unit: string;
  order: number;
};
type DraftStep = { id?: string; order: number; content: string };

type Draft = {
  name: string;
  baseServings: number | '';
  prepTime: number | '';
  notes: string;
  icon: string | null;
  category: Category | '';
  ingredients: DraftIngredient[];
  steps: DraftStep[];
  coverImage: string | null;
};

const EMPTY_DRAFT: Draft = {
  name: '',
  baseServings: '',
  prepTime: '',
  notes: '',
  icon: null,
  category: '',
  ingredients: [{ name: '', quantity: '', unit: '', order: 0 }],
  steps: [{ order: 0, content: '' }],
  coverImage: null,
};

function recipeToDraft(r: Recipe & { icon?: string | null; category?: Category | null }): Draft {
  return {
    name: r.name,
    baseServings: r.baseServings,
    prepTime: r.prepTime ?? '',
    notes: r.notes ?? '',
    icon: r.icon ?? null,
    category: r.category ?? '',
    ingredients: r.ingredients.map((i, idx) => ({
      id: i.id,
      name: i.name,
      quantity: i.quantity,
      unit: i.unit ?? '',
      order: i.order ?? idx,
    })),
    steps: r.steps.map((s, idx) => ({ id: s.id, order: s.order ?? idx, content: s.content })),
    coverImage: r.coverImage ?? null,
  };
}

function draftToPayload(d: Draft): RecipeInput {
  return {
    name: d.name.trim(),
    baseServings: d.baseServings === '' ? 2 : Number(d.baseServings),
    prepTime: d.prepTime === '' ? null : Number(d.prepTime),
    notes: d.notes.trim() || null,
    icon: d.icon,
    category: d.category === '' ? null : d.category,
    ingredients: d.ingredients
      .filter((i) => i.name.trim())
      .map((i, idx) => ({
        name: i.name.trim(),
        quantity: i.quantity === '' ? 0 : Number(i.quantity),
        unit: i.unit.trim() || null,
        order: idx,
      })),
    steps: d.steps
      .filter((s) => s.content.trim())
      .map((s, idx) => ({ order: idx, content: s.content.trim() })),
    tags: [],
  };
}

function labelFor(cat: Category): string {
  return cat
    .toLowerCase()
    .split(' ')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

export default function RecipeEditPage() {
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipeId, setRecipeId] = useState<string | null>(id ?? null);

  useEffect(() => {
    if (isCreate) return;
    api
      .get<Recipe>(`/api/v1/recipes/${id}`)
      .then((r) => {
        setDraft(recipeToDraft(r));
        setRecipeId(r.id);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'errore'))
      .finally(() => setLoading(false));
  }, [id, isCreate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = draftToPayload(draft);
      if (!payload.name) throw new ApiError('VALIDATION', 'Manca il nome.', 400);
      if (payload.ingredients.length === 0)
        throw new ApiError('VALIDATION', 'Aggiungi almeno un ingrediente.', 400);
      const saved = isCreate
        ? await api.post<Recipe>('/api/v1/recipes', payload)
        : await api.patch<Recipe>(`/api/v1/recipes/${id}`, payload);
      setRecipeId(saved.id);
      navigate(`/recipe/${saved.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'errore salvataggio');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <main className="max-w-xl mx-auto px-5 py-6 space-y-4">
        <div className="skeleton h-4 w-16" />
        <div className="skeleton h-10 w-3/4" />
      </main>
    );
  }

  const tagsForIcon = draft.category ? [draft.category.toLowerCase()] : [];

  return (
    <main className="max-w-xl mx-auto px-5 pt-5 pb-32">
      <nav className="mb-5 flex items-center justify-between">
        <Link to={isCreate ? '/' : `/recipe/${id}`} className="btn-ghost">← annulla</Link>
        <span className="text-xs text-muted font-medium">{isCreate ? 'Nuova ricetta' : 'Modifica'}</span>
      </nav>

      <form onSubmit={onSubmit} className="space-y-6">
        <section className="card p-5">
          <h2 className="text-sm font-bold text-ink mb-4">Copertina</h2>

          {!isCreate && recipeId ? (
            <PhotoUploader
              recipeId={recipeId}
              coverImage={draft.coverImage}
              onUpdated={(cover) => setDraft((d) => ({ ...d, coverImage: cover }))}
            />
          ) : (
            <p className="text-xs text-muted mb-4">
              Carica una foto dopo aver salvato la ricetta. Intanto scegli un'icona:
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-line">
            <IconPicker
              value={draft.icon}
              tags={tagsForIcon}
              name={draft.name}
              onChange={(emoji) => setDraft((d) => ({ ...d, icon: emoji }))}
            />
            <p className="text-[11px] text-muted mt-3 leading-relaxed">
              Senza foto mostriamo l'icona su uno sfondo colorato. La categoria e il nome
              suggeriscono l'icona giusta — puoi sovrascriverla quando vuoi.
            </p>
          </div>
        </section>

        <section className="card p-5 space-y-5">
          <Field label="Nome della ricetta" hint="come la chiami in famiglia">
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              required
              maxLength={200}
              placeholder="es. Pasta al pomodoro della nonna"
              className="input-bring text-lg font-semibold"
            />
          </Field>

          <Field label="Categoria" hint="a che tipo di piatto appartiene">
            <div className="relative">
              <select
                value={draft.category}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, category: e.target.value as Category | '' }))
                }
                className="input-bring appearance-none pr-12 bg-card"
              >
                <option value="">scegli una categoria…</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {labelFor(c)}
                  </option>
                ))}
              </select>
              <span
                className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-muted"
                aria-hidden
              >
                ▾
              </span>
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Porzioni base" hint="per quante persone è pensata">
              <input
                type="number"
                min={1}
                max={50}
                value={draft.baseServings}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    baseServings: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                placeholder="quante persone"
                className="input-bring tabular-nums"
              />
            </Field>
            <Field label="Tempo totale" hint="minuti">
              <input
                type="number"
                min={0}
                value={draft.prepTime}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    prepTime: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                placeholder="minuti"
                className="input-bring tabular-nums"
              />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <div className="mb-4">
            <h2 className="text-sm font-bold text-ink">Ingredienti</h2>
            <p className="text-xs text-muted mt-1">
              Scrivi cosa serve e in che quantità. Le unità possono essere <em>g, ml, q.b.</em> o
              lasciate vuote (per cose come <em>1 uovo</em>).
            </p>
          </div>
          <div className="grid grid-cols-[1fr_4.5rem_3.5rem_auto] gap-2 px-1 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-soft">ingrediente</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-soft text-right">qta</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-soft">unità</span>
            <span />
          </div>
          <ul className="space-y-2">
            {draft.ingredients.map((ing, idx) => (
              <li key={idx} className="grid grid-cols-[1fr_4.5rem_3.5rem_auto] gap-2 items-center">
                <input
                  type="text"
                  placeholder="es. farina"
                  className="input-line"
                  value={ing.name}
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.ingredients];
                      next[idx] = { ...next[idx], name: e.target.value };
                      return { ...d, ingredients: next };
                    })
                  }
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="quanto"
                  className="input-line text-right tabular-nums"
                  value={ing.quantity}
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.ingredients];
                      next[idx] = {
                        ...next[idx],
                        quantity: e.target.value === '' ? '' : Number(e.target.value),
                      };
                      return { ...d, ingredients: next };
                    })
                  }
                />
                <input
                  type="text"
                  placeholder="g"
                  className="input-line text-sm"
                  value={ing.unit}
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.ingredients];
                      next[idx] = { ...next[idx], unit: e.target.value };
                      return { ...d, ingredients: next };
                    })
                  }
                />
                <button
                  type="button"
                  className="h-8 w-8 rounded-full text-muted hover:bg-surface hover:text-brand flex items-center justify-center"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      ingredients: d.ingredients.filter((_, i) => i !== idx),
                    }))
                  }
                  aria-label="rimuovi ingrediente"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="mt-4 text-brand text-sm font-semibold"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                ingredients: [
                  ...d.ingredients,
                  { name: '', quantity: '', unit: '', order: d.ingredients.length },
                ],
              }))
            }
          >
            + aggiungi ingrediente
          </button>
        </section>

        <section className="card p-5">
          <div className="mb-4">
            <h2 className="text-sm font-bold text-ink">Passaggi</h2>
            <p className="text-xs text-muted mt-1">
              Scrivi i passaggi uno per uno: in <em>modalità cucina</em> saranno mostrati uno alla
              volta, a schermo intero.
            </p>
          </div>
          <ol className="space-y-4">
            {draft.steps.map((s, idx) => (
              <li key={idx} className="flex gap-3 items-start">
                <span className="shrink-0 h-8 w-8 rounded-full bg-brand text-white font-bold flex items-center justify-center text-sm">
                  {idx + 1}
                </span>
                <textarea
                  className="flex-1 input-bring min-h-[70px] leading-relaxed"
                  value={s.content}
                  placeholder={
                    idx === 0
                      ? "es. Porta a bollore l'acqua salata in una pentola capiente."
                      : 'cosa fare adesso?'
                  }
                  onChange={(e) =>
                    setDraft((d) => {
                      const next = [...d.steps];
                      next[idx] = { ...next[idx], content: e.target.value };
                      return { ...d, steps: next };
                    })
                  }
                />
                <button
                  type="button"
                  className="h-8 w-8 rounded-full text-muted hover:bg-surface hover:text-brand flex items-center justify-center mt-1"
                  onClick={() =>
                    setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }))
                  }
                  aria-label="rimuovi passaggio"
                >
                  ×
                </button>
              </li>
            ))}
          </ol>
          <button
            type="button"
            className="mt-4 text-brand text-sm font-semibold"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                steps: [...d.steps, { order: d.steps.length, content: '' }],
              }))
            }
          >
            + aggiungi passaggio
          </button>
        </section>

        <section className="card p-5">
          <Field label="Appunti" hint="facoltativo — trucchi, varianti, dosi per occasioni speciali">
            <textarea
              className="input-bring min-h-[100px] leading-relaxed"
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="es. il segreto è aggiungere un pizzico di zucchero al sugo"
            />
          </Field>
        </section>

        {error && <p className="text-brand font-medium">{error}</p>}

        <div className="sticky bottom-4">
          <button type="submit" className="btn-brand w-full py-4 text-base" disabled={saving}>
            {saving ? 'salvataggio…' : isCreate ? 'Aggiungi al ricettario' : 'Salva modifiche'}
          </button>
        </div>
      </form>
    </main>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink">{label}</span>
      {hint && <span className="block text-[11px] text-muted mt-0.5">{hint}</span>}
      <span className="block mt-2">{children}</span>
    </label>
  );
}
